import { useCallback, useState } from "react";

export interface UseImageUploadOptions {
  /**
   * Callback function called when image is successfully loaded
   * @param dataUrl - The base64 data URL of the image
   */
  onSuccess?: (dataUrl: string) => void;
  /**
   * Callback function called when image upload fails
   * @param error - The error that occurred
   */
  onError?: (error: Error) => void;
}

export interface UseImageUploadReturn {
  /**
   * The preview URL of the uploaded image (base64 data URL)
   */
  preview: string | null;
  /**
   * Handler function for file input onChange event
   */
  handleImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Function to reset/clear the preview
   */
  resetPreview: () => void;
  /**
   * Function to set preview directly (useful for initial values)
   */
  setPreview: (preview: string | null) => void;
  /**
   * The original uploaded file, if any
   */
  file: File | null;
}

/**
 * Custom hook for handling image uploads with preview
 * Converts uploaded image file to base64 data URL for preview
 *
 * @param options - Configuration options for the hook
 * @returns Object containing preview state and handlers
 *
 * @example
 * ```tsx
 * const { preview, handleImageUpload, resetPreview } = useImageUpload({
 *   onSuccess: (dataUrl) => {
 *     setValue("profileImage", dataUrl);
 *   }
 * });
 * ```
 */
export function useImageUpload(options?: UseImageUploadOptions): UseImageUploadReturn {
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const handleImageUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        const error = new Error("File must be an image");
        options?.onError?.(error);
        return;
      }

      // Reject SVG files explicitly
      if (file.type === "image/svg+xml") {
        const error = new Error("SVG files are not allowed. Please upload JPG, PNG or WebP.");
        options?.onError?.(error);
        return;
      }

      setFile(file);

      const reader = new FileReader();

      reader.onloadend = () => {
        try {
          const result = reader.result as string;
          setPreview(result);
          options?.onSuccess?.(result);
        } catch (error) {
          const err = error instanceof Error ? error : new Error("Failed to process image");
          options?.onError?.(err);
        }
      };

      reader.onerror = () => {
        const error = new Error("Failed to read file");
        options?.onError?.(error);
      };

      reader.readAsDataURL(file);
    },
    [options],
  );

  const resetPreview = useCallback(() => {
    setPreview(null);
    setFile(null);
  }, []);

  return {
    preview,
    handleImageUpload,
    resetPreview,
    setPreview,
    file,
  };
}
